/** Quiz Group Association API */
import { apiClient } from './client'
import type { ApiResponse } from '@/types/api'
import type {
  BiggoMigrationStatus,
  CurriculumModuleGroupSummary,
  QuizGroupAssociation,
} from '@/types/quizSet'

const QUIZ_ID_NUMBER_RE = /"quizId"\s*:\s*(\d+)/g

export function parseQuizGroupAssociationResponse(raw: string): QuizGroupAssociation[] {
  const normalized = raw.replace(QUIZ_ID_NUMBER_RE, '"quizId":"$1"')
  const parsed = JSON.parse(normalized) as ApiResponse<QuizGroupAssociation[]>
  if (!parsed.data) throw new Error('Invalid quiz group association response')
  return parsed.data
}

export function parseQuizGroupAssociationUpdateResponse(raw: string): QuizGroupAssociation {
  const normalized = raw.replace(QUIZ_ID_NUMBER_RE, '"quizId":"$1"')
  const parsed = JSON.parse(normalized) as ApiResponse<QuizGroupAssociation>
  if (!parsed.data) throw new Error('Invalid quiz group association update response')
  return parsed.data
}

export const getLessonQuizGroupAssociations = async (
  contentId: number,
  purpose = 'global_aiq',
  activeOnly = true,
): Promise<QuizGroupAssociation[]> => {
  const response = await apiClient.get<string>(`/quiz-group-associations/lesson/${contentId}`, {
    params: { purpose, active: activeOnly },
    responseType: 'text',
  })
  return parseQuizGroupAssociationResponse(response.data)
}

export const getQuizGroupAssociations = async (
  purpose = 'global_aiq',
  activeOnly = true,
): Promise<QuizGroupAssociation[]> => {
  const response = await apiClient.get<string>('/quiz-group-associations', {
    params: { purpose, active: activeOnly },
    responseType: 'text',
  })
  return parseQuizGroupAssociationResponse(response.data)
}

export const getCurriculumQuizGroupAssociations = async (
  curriculumId: number,
  purpose = 'global_aiq',
  activeOnly = true,
): Promise<QuizGroupAssociation[]> => {
  const response = await apiClient.get<string>(
    `/quiz-group-associations/curriculum/${curriculumId}/associations`,
    {
      params: { purpose, active: activeOnly },
      responseType: 'text',
    },
  )
  return parseQuizGroupAssociationResponse(response.data)
}

export const getCurriculumModuleGroupSummary = async (
  curriculumId: number,
  sourceSystem?: string,
): Promise<CurriculumModuleGroupSummary> => {
  const response = await apiClient.get<ApiResponse<CurriculumModuleGroupSummary>>(
    `/quiz-group-associations/curriculum/${curriculumId}/module-summary`,
    { params: { sourceSystem } },
  )
  if (!response.data.data) throw new Error('Invalid curriculum module summary response')
  return response.data.data
}

export const updateQuizGroupAssociationActive = async (
  associationId: number,
  isActive: boolean,
): Promise<QuizGroupAssociation> => {
  const response = await apiClient.patch<string>(
    `/quiz-group-associations/${associationId}/active`,
    { isActive },
    { responseType: 'text' },
  )
  return parseQuizGroupAssociationUpdateResponse(response.data)
}

export const updateQuizGroupBiggoMigrationStatus = async (
  associationId: number,
  status: BiggoMigrationStatus,
): Promise<QuizGroupAssociation> => {
  const response = await apiClient.patch<string>(
    `/quiz-group-associations/${associationId}/biggo-migration-status`,
    { status },
    { responseType: 'text' },
  )
  return parseQuizGroupAssociationUpdateResponse(response.data)
}
